import { differenceInYears, parseISO } from 'date-fns'

import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import { Note, NoteTypes } from '@lyrahealth-inc/shared-app-logic/src/models/notes/Notes'
import { MEETING_FORMATS } from '@lyrahealth-inc/shared-app-logic/src/models/session/Session'

import { LICENSED_NP_ROLES } from './asyncTimeNote'
import { hasRole } from '../../../common/utils/utils'

// One template for every NP visit type: the first question picks the session type and the rest of the form
// is conditioned on it.
export const NP_SESSION_NOTE_NAME = 'npSessionNote'

export const NP_INTAKE_SESSION_TYPE = 'intake'

export const NP_FEEDBACK_SESSION_TYPE = 'feedback'

// Session types that have their questions in the template. The others cannot be signed yet.
export const NP_SESSION_TYPES_WITH_FORM: string[] = [NP_INTAKE_SESSION_TYPE, NP_FEEDBACK_SESSION_TYPE]

type TemplateCondition = { items?: TemplateCondition[]; parentField?: string; parentValue?: unknown }

const conditionNamesSessionType = (condition: TemplateCondition | undefined, sessionType: string): boolean =>
  !!condition &&
  (condition.items
    ? condition.items.some((item) => conditionNamesSessionType(item, sessionType))
    : condition.parentField === 'sessionType' &&
      Array.isArray(condition.parentValue) &&
      condition.parentValue.includes(sessionType))

// A note keeps the template it was saved with, which can predate the questions of a session type the code supports.
export const hasNpTemplateQuestions = (noteMetadata: Note['content_meta_data'], sessionType: string) => {
  const properties = noteMetadata?.meta_data?.schema?.properties as
    | Record<string, { condition?: TemplateCondition }>
    | undefined
  return (
    !properties || Object.values(properties).some(({ condition }) => conditionNamesSessionType(condition, sessionType))
  )
}

/**
 * Single seam for ENGBEND-13180: once NP appointments carry a real session type, return it here and
 * the note opens with the question already answered.
 */
export const getNpVisitType = (_appointment?: Appointment): string | undefined => undefined

export const getNpModality = (meetingFormat?: MEETING_FORMATS): 'In Person' | 'Virtual' | undefined => {
  switch (meetingFormat) {
    case MEETING_FORMATS.IN_PERSON:
    case MEETING_FORMATS.IN_PERSON_UNDERSCORE:
      return 'In Person'
    case MEETING_FORMATS.VIDEO:
    case MEETING_FORMATS.PHONE:
    case MEETING_FORMATS.CALL:
      return 'Virtual'
    default:
      return undefined
  }
}

// Age is taken at the session, so a signed note keeps the answers it was signed with after a birthday.
export const getNpSessionNoteExternalValues = (
  dateOfBirth?: string | null,
  sessionDate: Date = new Date(),
  roles?: string[],
  isNoteCompleted = false,
) => {
  const age = dateOfBirth ? differenceInYears(sessionDate, parseISO(dateOfBirth)) : undefined
  return {
    isMinor: age !== undefined && age < 18,
    isAbove14: age !== undefined && age > 14,
    // Only licensed NP roles write the risk fields, but everyone who can see a signed note reads them.
    isLicensed: isNoteCompleted || hasRole(roles, LICENSED_NP_ROLES),
    // `isBendClient` is left undefined on purpose until PP-API returns BEND (ENGBEND-13586): the template then
    // shows the Bend recording notice and never the Lyra one.
    // Diagnosis sections stay hidden until ENGBEND-13551 supplies the customer type.
    showFormalDiagnoses: false,
    showProvisionalDiagnosis: false,
  }
}

// The NP Intake is a session note (not `initial`) of the NP template, told apart by its session type. A no-show,
// a cancelled session or a client who did not consent is not an intake that took place.
export const isIntakeNote = (note: Pick<Note, 'note_type' | 'content_meta_data' | 'body'>) =>
  note.note_type === NoteTypes.INITIAL ||
  (note.content_meta_data?.name === NP_SESSION_NOTE_NAME &&
    note.body?.sessionType === NP_INTAKE_SESSION_TYPE &&
    note.body?.sessionHappened !== 'notCompleted' &&
    note.body?.verbalConsent !== 'No')
